import {
  getWpNumber,
  getWpPrefix,
  getWpCaption,
  getLocationName,
  getInspectionType,
  getMiscMaterialsUnitOfMeasure,
  getTestType,
} from "../adrUtilsV3";
import "./adrPdfTypeDefsV3";

// Produces the data object to be consumed by renderSection()
// Implements a facade that flattens complex feeder data
// into primitive types, and fetches any necessary related fields.

// Interface
const adrPdfSectionDataV3 = {
  headerData,
  descOfWork,
  wpHours,
  generalComments,
  attachments,
};

/**
 * Package Adr header data for PDF
 * @param {Object} inputData
 */
function headerData(adr, feederLists, version) {
  const { jobStamp, projContractorList } = feederLists;
  const contractorSet = new Set();
  const weatherConditions = ["Clear", "Overcast", "Foggy", "Rain", "Snow"];

  Object.values(adr.laborSetClient).forEach((lsr) => {
    if (lsr.contractorId) {
      const contractor = (projContractorList || []).find(
        (c) => c.contractorId === lsr.contractorId
      );
      const listString = contractor
        ? `(${contractor.isPrime ? "P" : "S"}) ${contractor.name} (${
            contractor.abbreviation
          })`
        : "(N/A)";
      contractorSet.add(listString);
    }
  });

  return {
    version,
    writtenBy:
      adr.authorFirstName && adr.authorLastName
        ? adr.authorFirstName.charAt(0) + adr.authorLastName.charAt(0)
        : "",
    reviewedBy: 0,
    contractors: Array.from(contractorSet),
    reportNo: adr.reportNo + (adr.reportNoSuffix ? adr.reportNoSuffix : ""),
    reportStartDate: adr.reportStartDate, // JS Date
    reportEndDate: adr.reportEndDate, // JS Date
    customCategory: adr.customCategory || "",
    contractorStartShift: adr.contractorStartShift,
    contractorEndShift: adr.contractorEndShift,
    engineerStartShift: adr.engineerStartShift,
    engineerEndShift: adr.engineerEndShift,
    isNightWork:
      /* parseInt(adr.engineerEndShift) < parseInt(adr.engineerStartShift) ? "Yes" : "No", */
      parseInt(adr.engineerStartShift) <= 200 || parseInt(adr.engineerStartShift) >= 1800 || parseInt(adr.engineerEndShift) >= 2200 || parseInt(adr.engineerEndShift) <= 600 ? "Yes" : "No",
    engineerOT: adr.engineerOT,
    engineerOTReason: adr.engineerOTReason || "",
    weatherCondition: weatherConditions[adr.weatherCondition] || "",
    weatherComment: adr.weatherComment,
    lowTemp: adr.lowTemp != null ? adr.lowTemp : "",
    highTemp: adr.highTemp != null ? adr.highTemp : "",
    jobStamp,
  };
}

function descOfWork(adr, feederLists) {
  const {
    projItemList,
    projCcoList,
    projPendingIssueList,
    projLocationList,
    miscMaterialsUnitsOfMeasure,
    fieldTestTypes,
  } = feederLists;
  let data = [];
  Object.values(adr.workPerformedClient).forEach((wpc) =>
    wpc.locationWorkPerformed
      .filter((locWp) => locWp.locationId)
      .forEach((locWp) => {
        data.push({
          wpPrefix: getWpPrefix(wpc),
          wpNumber: getWpNumber(wpc, projItemList, projCcoList, projPendingIssueList),
          wpFullDescription: getWpCaption(
            wpc,
            projItemList,
            projCcoList,
            projPendingIssueList
          ),
          locationName: locWp.isSublocation
            ? getLocationName(locWp.sublocationId, projLocationList)
            : getLocationName(locWp.locationId, projLocationList),
          inspectionType: getInspectionType(locWp.inspectionType),
          workDescription: locWp.workDescription,
          quantity: locWp.quantity,
          unitOfMeasure: wpc.unitOfMeasure,
          isSublocation: locWp.isSublocation,
          sublocationWorkPerformedType: locWp.sublocationWorkPerformedType,
          stationFromOrAt: locWp.isSublocation ? locWp.stationFromOrAt : "",
          stationFromOrAtOffsetValue: locWp.isSublocation
            ? locWp.stationFromOrAtOffsetValue
            : "",
          stationFromOrAtOffsetDirection: locWp.isSublocation
            ? locWp.stationFromOrAtOffsetDirection
            : "",
          stationTo: locWp.isSublocation ? locWp.stationTo : "",
          stationToOffsetValue: locWp.isSublocation ? locWp.stationToOffsetValue : "",
          stationToOffsetDirection: locWp.isSublocation
            ? locWp.stationToOffsetDirection
            : "",
          miscMaterials: locWp.miscMaterials.map((lmm) => ({
            materialDescription: lmm.materialDescription,
            quantity: lmm.quantity,
            unitOfMeasure: getMiscMaterialsUnitOfMeasure(
              +lmm.miscMaterialsUnitOfMeasureId,
              miscMaterialsUnitsOfMeasure
            ),
          })),
          testsPerformed: locWp.testsPerformed.map((ltp) => ({
            testType: getTestType(+ltp.fieldTestTypeId, fieldTestTypes),
            numberOfTestsOrSamplesTaken: ltp.numberOfTestsOrSamplesTaken,
            remarks: ltp.remarks,
          })),
          attachments:
            locWp.attachments &&
            locWp.attachments
              .filter((locWpa) => !locWpa.isDeleted)
              .map((locWpa) => {
                if (locWpa.originalFileName) return locWpa.originalFileName;
                else return locWpa.filePath.replace(/^.*[\\/]/, "");
              }),
        });
      })
  );

  return data;
}

/**
 * Package Wp hours and remarks for both labor and equipment
 * @param {import("../../../../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} adr
 * @param {import("../../../../_typedefs/adrV3TypeDefs").WpSortOrder[]} wpSortOrder
 * @param {Object} feederLists
 * @returns {WpHours}
 */
function wpHours(adr, wpSortOrder, feederLists) {
  const {
    projItemList,
    projCcoList,
    projPendingIssueList,
    projLaborList,
    projContractorList,
    projEquipList,
    tradeList,
  } = feederLists;
  /** @type {Object.<number, PdfWorkPerformed>} */
  let pdfWorkPerformed = {};
  wpSortOrder.forEach((wpSo) => {
    const wp = adr.workPerformedClient[wpSo.wpTabId];
    const wpPrefix = getWpPrefix(wp);
    const wpNumber = getWpNumber(wp, projItemList, projCcoList, projPendingIssueList);
    pdfWorkPerformed = {
      ...pdfWorkPerformed,
      [wpSo.wpTabId]: { wpPrefix, wpNumber, isIdle: wp.isIdle },
    };
  });

  /** @type {PdfLaborSetRow[]} */
  let pdfLaborSet = [];

  /** @type {PdfLaborRemarks[]} */
  let pdfLaborRemarks = [];

  // Remarks counter: only increments when there are remarks in current lsr
  let laborRemarksIndex = 0;

  // Since laborSetClient's row order needs to be reflected in the PDF, loop thru
  // laborSetOrder, which is an ordered set of lsrKeys (instead of looping thru
  // Object.values(laborSetClient)).
  adr.laborSetOrder.forEach((lsrKey) => {
    const lsr = adr.laborSetClient[lsrKey];
    if (lsr.laborId && lsr.tradeId && lsr.contractorId) {
      const labor = (projLaborList || []).find((l) => l.laborId === lsr.laborId);
      const trade = (tradeList || []).find((t) => t.tradeId === lsr.tradeId);
      const contractor = (projContractorList || []).find(
        (c) => c.contractorId === lsr.contractorId
      );
      const otExists = Object.values(lsr.laborHours).some((lh) => lh.otHours > 0);

      let remarksKey = "";

      // Build pdfLaborRemarks BEFORE pdfLaborSet, so pdfLaborSet can utilize remarksKey
      // set by below.
      if (lsr.remarks.remarks) {
        laborRemarksIndex++;
        remarksKey = `L-${laborRemarksIndex}`;
        pdfLaborRemarks.push({
          remarksKey,
          remarks: lsr.remarks.remarks,
        });
      }

      pdfLaborSet.push({
        contractorAbbrev: contractor ? contractor.abbreviation || "(N/A)" : "(N/A)",
        fullName: labor ? labor.fullName || "(N/A)" : "(N/A)",
        trade: trade ? trade.title || "(N/A)" : "(N/A)",
        otExists,
        laborHours: lsr.laborHours,
        remarksKey,
      });
    }
  });

  /**
   * Do same as above for equipment.
   * @type {PdfEquipmentSetRow[]}
   */
  let pdfEquipmentSet = [];

  /** @type {PdfEquipmentRemarks[]} */
  let pdfEquipmentRemarks = [];

  // Remarks counter: only increments when there is remarks or operators
  let equipmentRemarksIndex = 0;

  adr.equipmentSetOrder.forEach((esrKey) => {
    const esr = adr.equipmentSetClient[esrKey];
    if (esr.equipmentId && esr.contractorId) {
      const equip = (projEquipList || []).find((e) => e.equipmentId === esr.equipmentId);
      const contractor = (projContractorList || []).find(
        (c) => c.contractorId === esr.contractorId
      );
      const otExists = Object.values(esr.equipmentHours).some((eh) => eh.otHours > 0);

      const operatorData = Object.values(esr.equipmentHours)
        .filter((eh) => eh.operatorId)
        .map((eh) => {
          const operator = (projLaborList || []).find((l) => l.laborId === eh.operatorId);
          const wpShortCaption = adr.workPerformedClient[eh.wpTabId].wpShortCaption;
          return `${wpShortCaption} - ${
            operator ? operator.fullName : "(Operator Data N/A)"
          }`;
        });

      let remarksKey = "";

      if (esr.remarks.remarks || operatorData.length > 0) {
        equipmentRemarksIndex++;
        remarksKey = `E-${equipmentRemarksIndex}`;
        pdfEquipmentRemarks.push({
          remarks: esr.remarks.remarks,
          operatorData: operatorData.length > 0 ? operatorData : [],
          remarksKey,
        });
      }

      pdfEquipmentSet.push({
        contractorAbbrev: contractor ? contractor.abbreviation || "(N/A)" : "(N/A)",
        numberMakeDesc: equip ? equip.numberMakeDesc : "(N/A)",
        otExists,
        equipmentHours: esr.equipmentHours,
        remarksKey,
      });
    }
  });

  return {
    wpSortOrder,
    pdfWorkPerformed,
    pdfLaborSet,
    pdfLaborRemarks,
    pdfEquipmentSet,
    pdfEquipmentRemarks,
  };
}

function generalComments(generalComments, feederLists) {
  const { projKeywordList } = feederLists;
  // Create a copy of inputData.generalComments and add
  // 'attachments' property, which is an array of filenames
  return generalComments
    .filter((gc) => !(!gc.subject && !gc.narrative && !gc.keywordIds.length === 0))
    .map((gc) => {
      const keywordsForPrint = gc.keywordIds.map(
        (kwId) => (projKeywordList || []).find((k) => k.keywordId === kwId).keywordTag
      );

      return {
        ...gc,
        keywordsForPrint,
        attachments: gc.commentAttachments
          ? gc.commentAttachments
              .filter((gca) => !gca.isDeleted)
              .map((gca) => {
                if (gca.originalFileName) return gca.originalFileName;
                else return gca.filePath.replace(/^.*[\\/]/, "");
              })
          : null,
      };
    });
}

function attachments() {}

export default adrPdfSectionDataV3;
